import { driver } from 'driver.js';

const DEFAULT_DRIVER_BUTTONS = Object.freeze(['previous', 'next', 'close']);

function toDriverPopover(step = {}) {
    return {
        title: step.title || '',
        description: step.content || '',
        side: step.side,
        align: step.align,
        showButtons: step.showButtons,
        showProgress: step.showProgress,
        disableButtons: step.disableButtons,
        popoverClass: step.popoverClass,
        progressText: step.progressText,
        doneBtnText: step.doneBtnText,
        nextBtnText: step.nextBtnText,
        prevBtnText: step.prevBtnText,
        onPopoverRender: step.onPopoverRender,
        onNextClick: step.onNextClick,
        onPrevClick: step.onPrevClick,
        onCloseClick: step.onCloseClick,
    };
}

function resolveElementFromTarget(target) {
    if (!target) {
        return null;
    }

    if (typeof target === 'function') {
        return resolveElementFromTarget(target());
    }

    if (typeof Element !== 'undefined' && target instanceof Element) {
        return target;
    }

    if (Array.isArray(target)) {
        for (const entry of target) {
            const resolved = resolveElementFromTarget(entry);
            if (resolved) {
                return resolved;
            }
        }

        return null;
    }

    if (typeof target === 'string') {
        return document.querySelector(target);
    }

    return null;
}

export async function waitForTourElement(
    target,
    { timeoutMs = 2400, intervalMs = 120 } = {}
) {
    const start = Date.now();

    while (Date.now() - start <= timeoutMs) {
        const element = resolveElementFromTarget(target);
        if (element) {
            return element;
        }

        await new Promise((resolve) => {
            window.setTimeout(resolve, intervalMs);
        });
    }

    return null;
}

export async function buildDriverSteps(
    steps = [],
    waitOptions = {}
) {
    const resolvedSteps = [];

    for (const step of steps) {
        if (!step?.target) {
            resolvedSteps.push({
                popover: toDriverPopover(step),
            });
            continue;
        }

        const element = await waitForTourElement(step.target, waitOptions);
        if (!element) {
            continue;
        }

        resolvedSteps.push({
            element,
            disableActiveInteraction: step.disableActiveInteraction,
            popover: toDriverPopover(step),
        });
    }

    return resolvedSteps;
}

export function createDriverTour(config = {}) {
    return driver({
        animate: true,
        smoothScroll: true,
        allowClose: true,
        overlayClickBehavior: 'close',
        allowKeyboardControl: true,
        showButtons: [...DEFAULT_DRIVER_BUTTONS],
        showProgress: true,
        prevBtnText: 'Back',
        nextBtnText: 'Next',
        doneBtnText: 'Finish Tour',
        ...config,
    });
}

export function startDriverTour({
    steps = [],
    config = {},
    createDriver = createDriverTour,
    onCompleted = () => {},
    onDismissed = () => {},
    onSettled = () => {},
} = {}) {
    if (!steps.length) {
        return null;
    }

    let completed = false;
    let driverInstance = null;
    const {
        onDestroyed: configOnDestroyed,
        onNextClick: configOnNextClick,
        onPrevClick: configOnPrevClick,
        onCloseClick: configOnCloseClick,
        ...restConfig
    } = config;

    driverInstance = createDriver({
        ...restConfig,
        steps,
        onNextClick: (element, step, opts) => {
            if (typeof configOnNextClick === 'function') {
                configOnNextClick(element, step, opts);
                return;
            }

            if (opts.driver.isLastStep()) {
                completed = true;
                onCompleted(opts);
                opts.driver.destroy();
                return;
            }

            opts.driver.moveNext();
        },
        onPrevClick: (element, step, opts) => {
            if (typeof configOnPrevClick === 'function') {
                configOnPrevClick(element, step, opts);
                return;
            }

            opts.driver.movePrevious();
        },
        onCloseClick: (element, step, opts) => {
            if (typeof configOnCloseClick === 'function') {
                configOnCloseClick(element, step, opts);
                return;
            }

            opts.driver.destroy();
        },
        onDestroyed: (element, step, opts) => {
            if (!completed) {
                onDismissed(opts);
            }

            onSettled({
                completed,
                config: opts.config,
                state: opts.state,
                driver: opts.driver,
            });

            if (typeof configOnDestroyed === 'function') {
                configOnDestroyed(element, step, opts);
            }
        },
    });

    driverInstance.drive();
    return driverInstance;
}
